import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { Lock, ShieldAlert } from 'lucide-react';
import '../shared/shared-chrome.css';
import { useRole } from '../../hooks/useRole';
import { useUrlState } from '../../hooks/useUrlState';
import { useTimeFilterContext } from '../../contexts/TimeFilterContext';
import logger from '../../lib/logger';
import PageHeader from '../shared/PageHeader';
import VendorPicker from '../VendorCompetitiveAnalysis/VendorPicker';
import SnapshotStrip from '../VendorCompetitiveAnalysis/SnapshotStrip';
import TrajectoryCharts from '../VendorCompetitiveAnalysis/TrajectoryCharts';
import PenetrationHeatmap from '../VendorCompetitiveAnalysis/PenetrationHeatmap';
import SecurityCoverageMatrix from '../VendorCompetitiveAnalysis/SecurityCoverageMatrix';
import CompetitiveMap from '../VendorCompetitiveAnalysis/CompetitiveMap';
import DisplacementTable from '../VendorCompetitiveAnalysis/DisplacementTable';
import RenewalCalendar from '../VendorCompetitiveAnalysis/RenewalCalendar';
import TakeoutView from '../VendorCompetitiveAnalysis/TakeoutView';
import PartnerEcosystem from '../VendorCompetitiveAnalysis/PartnerEcosystem';
import PartnerCategorySpend from '../VendorCompetitiveAnalysis/PartnerCategorySpend';
import PlanKanban from '../VendorCompetitiveAnalysis/PlanKanban';
import StrategicInitiatives from '../VendorCompetitiveAnalysis/StrategicInitiatives';
import { loggedMutation, apiClient } from '../VendorCompetitiveAnalysis/loggedFetch';
import VendorsTabs, { useVendorsTabs } from './VendorsTabs';
import VendorsDrilldown from './VendorsDrilldown';
import '../VendorCompetitiveAnalysis/VendorCompetitiveAnalysis.css';
import '../VendorCompetitiveAnalysis/PenetrationHeatmap.css';
import './Vendors.css';
import {
  fiscalYearOf,
  fiscalYearRange,
  formatFiscalYear,
  fiscalSpanLabel,
} from '@techstack/core';
import { useFiscalBasisContext } from '../../contexts/FiscalBasisContext';

/*
 * VendorsPage — /vendors. GAS-parity Vendors section (P6): the GAS
 * VendorAnalysisView anatomy (vendor selector → KPI snapshot strip →
 * nine tabs → one shared transactions drilldown) re-hosted on the
 * richer TekStak panels from components/VendorCompetitiveAnalysis.
 * VendorCompetitiveAnalysis.js itself is untouched — this page imports
 * its child components directly and copies their prop wiring.
 *
 * Kept GAS-clean on purpose: no strict/low-confidence toggle chips
 * (strict vendor evidence stays default-ON), no ProductFamilyChips,
 * no list-scoping chrome — statewide view scoped only by the global
 * time filter.
 */

// Fixed evidence settings (VCA defaults; no toggles in v1).
const STRICT = true;
const INCLUDE_LOW_CONFIDENCE = false;
const PRODUCT_FAMILY = null;
// Statewide — no list/department scoping chrome in v1.
const DEPARTMENT_IDS = null;
const LIST_NAME = undefined;

function VendorsPage() {
  const { isSuperAdmin, roleLoading, isFeatureEnabled } = useRole();
  const hasAccess = isSuperAdmin || isFeatureEnabled('vendor_analysis');
  // Global time filter from context — same derivation as VCA: the shell
  // TimeFilterContext exposes `years`, translated once in useTimeFilter.
  const { years } = useTimeFilterContext();
  // Vendor lives in the URL so hard-refresh / bookmark / share restore
  // the same view (same pattern as /vendor-analysis).
  const [vendor, setVendor] = useUrlState('vendor', null);
  const [drilldown, setDrilldown] = useState(null);
  const [activeTab, setActiveTab] = useVendorsTabs();
  const queryClient = useQueryClient();

  // Default to Palo Alto Networks on first visit (all roles — GAS parity:
  // its Vendors tab always opens with a vendor loaded, never an empty
  // "pick a vendor" state). Clearing the picker afterwards is still allowed.
  const [hasSetDefault, setHasSetDefault] = useState(false);
  useEffect(() => {
    if (!hasSetDefault && !roleLoading && !vendor) {
      setVendor('Palo Alto Networks');
      setHasSetDefault(true);
    }
  }, [hasSetDefault, roleLoading, vendor, setVendor]);

  useEffect(() => {
    logger.vendorAnalysis.info('VendorsPage:mounted', { hasAccess, roleLoading });
    return () => logger.vendorAnalysis.info('VendorsPage:unmounted');
  }, [hasAccess, roleLoading]);

  useEffect(() => {
    if (vendor) {
      logger.vendorAnalysis.info('VendorsPage:vendor-changed', { vendor, tab: activeTab });
    }
  }, [vendor, activeTab]);

  // Label fiscal-year drilldowns on the VIEWER's calendar.
  const { calendar: fiscalCalendar } = useFiscalBasisContext();

  // Build a context label for the drill-down header from the drilldown shape.
  const drilldownContext = useMemo(() => {
    if (!drilldown) return null;
    const parts = [];
    if (drilldown.department) parts.push(drilldown.department);
    if (drilldown.startDate && drilldown.endDate) {
      const sy = new Date(drilldown.startDate).getFullYear();
      const ey = new Date(drilldown.endDate).getFullYear();
      // A full fiscal year on the ACTIVE calendar gets the "FY27 (Aug 2026 –
      // Jul 2027)" treatment. This used to pattern-match -07-01/-06-30, so a
      // PANW-basis drilldown fell through to a bare "2026–2027".
      const fy = fiscalYearOf(drilldown.startDate, fiscalCalendar);
      const span = fiscalYearRange(fy, fiscalCalendar);
      if (drilldown.startDate === span.start && drilldown.endDate === span.end) {
        const [startMon, endMon] = fiscalSpanLabel(fiscalCalendar).split('–');
        parts.push(
          `${formatFiscalYear(fy, fiscalCalendar)} (${startMon} ${sy} – ${endMon} ${ey})`
        );
      } else {
        parts.push(`${sy}–${ey}`);
      }
    }
    return parts.length > 0 ? `· ${parts.join(' · ')}` : null;
  }, [drilldown, fiscalCalendar]);

  // Cross-tab "seed a plan card" mutation — same wiring as VCA: heatmap,
  // take-out, and partners drop a card into the 30-60-90 backlog, then we
  // flip to the plan tab so the user sees where it landed.
  const seedPlan = useMutation({
    mutationFn: loggedMutation(
      'VendorsPage:seed-plan',
      ({ payload }) => apiClient.post(
        `/api/vendor-analysis/${encodeURIComponent(vendor)}/plan/cards`,
        payload
      )
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-analysis', 'plan', vendor] });
      setActiveTab('plan');
    },
  });

  const handleSeedPlan = useCallback((seed) => {
    if (!vendor || !seed) return;
    seedPlan.mutate({
      payload: {
        column_key: 'backlog',
        title: seed.title,
        rationale: seed.rationale || null,
        dollar_impact: seed.dollar_impact || null,
        due_date: seed.due_date || null,
        seed_kind: seed.kind || 'manual',
        seed_payload: seed,
      },
    });
  }, [vendor, seedPlan]);

  // Shared props for the evidence-filtered panels (copied from the VCA
  // prop wiring, with the fixed defaults above).
  const panelProps = {
    vendor,
    includeLowConfidence: INCLUDE_LOW_CONFIDENCE,
    strict: STRICT,
    productFamily: PRODUCT_FAMILY,
    departmentIds: DEPARTMENT_IDS,
    listName: LIST_NAME,
    years,
    onDrilldown: setDrilldown,
  };

  if (roleLoading) {
    return <div className="vendors-page vendors-loading">Checking access…</div>;
  }

  if (!hasAccess) {
    return (
      <div className="vendors-page vendors-denied">
        <Lock size={32} />
        <h2>Restricted</h2>
        <p>This page requires Vendor Analysis access.</p>
      </div>
    );
  }

  return (
    <div className="vendors-page">
      <PageHeader title="Vendors" blurb="Spend, heatmaps, renewals" />

      <VendorPicker value={vendor} onChange={setVendor} />

      {vendor ? (
        <>
          <SnapshotStrip {...panelProps} />
          <VendorsTabs activeKey={activeTab} onChange={setActiveTab} />

          <div className="vendors-panel">
            {activeTab === 'trajectory' && (
              <TrajectoryCharts {...panelProps} />
            )}

            {activeTab === 'heatmap' && (
              <PenetrationHeatmap {...panelProps} onSeedPlan={handleSeedPlan} />
            )}

            {activeTab === 'security' && (
              <SecurityCoverageMatrix
                departmentIds={DEPARTMENT_IDS}
                vendor={vendor}
                onDrilldown={setDrilldown}
              />
            )}

            {activeTab === 'competitive' && (
              <>
                <CompetitiveMap {...panelProps} />
                <DisplacementTable {...panelProps} />
              </>
            )}

            {activeTab === 'renewals' && (
              <RenewalCalendar {...panelProps} />
            )}

            {activeTab === 'takeout' && (
              <TakeoutView
                vendor={vendor}
                includeLowConfidence={INCLUDE_LOW_CONFIDENCE}
                strict={STRICT}
                productFamily={PRODUCT_FAMILY}
                departmentIds={DEPARTMENT_IDS}
                listName={LIST_NAME}
                years={years}
                onSeedPlan={handleSeedPlan}
              />
            )}

            {activeTab === 'partners' && (
              <>
                <PartnerCategorySpend
                  vendor={vendor}
                  departmentIds={DEPARTMENT_IDS}
                  onDrilldown={setDrilldown}
                />
                <PartnerEcosystem
                  vendor={vendor}
                  includeLowConfidence={INCLUDE_LOW_CONFIDENCE}
                  strict={STRICT}
                  productFamily={PRODUCT_FAMILY}
                  departmentIds={DEPARTMENT_IDS}
                  listName={LIST_NAME}
                  years={years}
                  onSeedPlan={handleSeedPlan}
                />
              </>
            )}

            {activeTab === 'plan' && (
              <PlanKanban
                vendor={vendor}
                includeLowConfidence={INCLUDE_LOW_CONFIDENCE}
                strict={STRICT}
              />
            )}

            {activeTab === 'initiatives' && (
              <StrategicInitiatives vendor={vendor} />
            )}
          </div>
        </>
      ) : (
        <div className="vendors-empty">
          <ShieldAlert size={28} />
          <p>Pick a vendor above to load the analysis.</p>
        </div>
      )}

      {drilldown && (
        <VendorsDrilldown
          drilldown={drilldown}
          vendor={vendor}
          drilldownContext={drilldownContext}
          strict={STRICT}
          includeLowConfidence={INCLUDE_LOW_CONFIDENCE}
          productFamily={PRODUCT_FAMILY}
          onClose={() => {
            logger.vendorAnalysis.info('VendorsPage:drilldown-closed');
            setDrilldown(null);
          }}
        />
      )}
    </div>
  );
}

export default VendorsPage;
